import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { userApi } from '../lib/api'
import LoadingSpinner from '../components/LoadingSpinner'
import PromptManager from '../components/PromptManager'
import MultiScreenshotPromptEditor from '../components/MultiScreenshotPromptEditor'
import LiveSessionView from '../components/LiveSessionView'
import HelpModal from '../components/HelpModal'

const DashboardPage: React.FC = () => {
  const { user, logout } = useAuth()
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<'config' | 'live'>('config')

  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false)

  // Handle keyboard shortcut to open help modal
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === '?' && !event.ctrlKey && !event.altKey && !event.metaKey) {
        // Only trigger if not in an input field
        const target = event.target as HTMLElement
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA' && !target.isContentEditable) {
          event.preventDefault()
          setIsHelpModalOpen(true)
        }
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [])

  const { data: config, isLoading, error } = useQuery({
    queryKey: ['userConfig'],
    queryFn: userApi.getConfig,
  })

  const updateDefaultPromptMutation = useMutation({
    mutationFn: userApi.updateDefaultPrompt,
    onSuccess: () => {
      console.log('Default prompt update successful, invalidating cache...')
      // Add a small delay to ensure the backend has processed the update
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['userConfig'] })
      }, 100)
    },
    onError: (error) => {
      console.error('Failed to update default prompt:', error)
      // You could add a toast notification here
    },
  })

  const addAlternativePromptMutation = useMutation({
    mutationFn: userApi.addAlternativePrompt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfig'] })
    },
  })

  const updateAlternativePromptMutation = useMutation({
    mutationFn: ({ id, prompt }: { id: string; prompt: string }) =>
      userApi.updateAlternativePrompt(id, prompt),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfig'] })
    },
  })

  const deleteAlternativePromptMutation = useMutation({
    mutationFn: userApi.deleteAlternativePrompt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfig'] })
    },
  })

  const updateMultiScreenshotPromptMutation = useMutation({
    mutationFn: userApi.updateMultiScreenshotPrompt,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfig'] })
    },
  })

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error) {
    console.error('DashboardPage: Error loading configuration:', error)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error Loading Configuration</h2>
          <p className="text-gray-600 mb-4">Failed to load your user configuration.</p>
          <details className="mb-4 text-left">
            <summary className="cursor-pointer text-sm text-gray-500">Error Details</summary>
            <pre className="mt-2 text-xs text-red-600 bg-red-50 p-2 rounded">
              {JSON.stringify(error, null, 2)}
            </pre>
          </details>
          <button
            onClick={() => window.location.reload()}
            className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Synapse User Portal</h1>
              <p className="text-gray-600">Welcome back, {user?.username}</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsHelpModalOpen(true)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Keyboard shortcuts (Press ? for help)"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <a
                href="/analysis"
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
                title="Enter a distraction-free view for interview prep"
              >
                Focus View
              </a>
              <a
                href="/results"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                View Results
              </a>
              <button
                onClick={logout}
                className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('config')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'config'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Configuration
            </button>
            <button
              onClick={() => setActiveTab('live')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'live'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Latest Result
            </button>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {activeTab === 'config' ? (
            <div className="grid grid-cols-1 gap-6">
            {/* Default Prompt Section */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Default Prompt
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  This prompt will be used by default when processing your screenshots.
                </p>
                <DefaultPromptEditor
                  defaultPrompt={config?.defaultPrompt || ''}
                  onSave={(prompt) => updateDefaultPromptMutation.mutate(prompt)}
                  isLoading={updateDefaultPromptMutation.isPending}
                  isSuccess={updateDefaultPromptMutation.isSuccess}
                />
              </div>
            </div>

            {/* Multi-Screenshot Session Prompt Section */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Multi-Screenshot Session Prompt
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  This specialized prompt will be used when processing multi-screenshot sessions (CTRL+SHIFT+5 → CTRL+SHIFT+6 → CTRL+SHIFT+7).
                </p>
                <MultiScreenshotPromptEditor
                  multiScreenshotPrompt={config?.multiScreenshotPrompt || ''}
                  onSave={(prompt) => updateMultiScreenshotPromptMutation.mutate(prompt)}
                  isLoading={updateMultiScreenshotPromptMutation.isPending}
                />
              </div>
            </div>

            {/* Alternative Prompts Section */}
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Alternative Prompts
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Create alternative prompts for reprocessing screenshots with different analysis approaches.
                </p>
                <PromptManager
                  prompts={config?.alternativePrompts || []}
                  onAdd={(prompt) => addAlternativePromptMutation.mutate(prompt)}
                  onUpdate={(id, prompt) => updateAlternativePromptMutation.mutate({ id, prompt })}
                  onDelete={(id) => deleteAlternativePromptMutation.mutate(id)}
                  isLoading={
                    addAlternativePromptMutation.isPending ||
                    updateAlternativePromptMutation.isPending ||
                    deleteAlternativePromptMutation.isPending
                  }
                />
              </div>
            </div>
          </div>
          ) : (
            <div className="fixed inset-0 top-[140px] bg-white">
              <LiveSessionView />
            </div>
          )}
        </div>
      </main>

      {/* Help Modal */}
      <HelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
    </div>
  )
}

interface DefaultPromptEditorProps {
  defaultPrompt: string
  onSave: (prompt: string) => void
  isLoading: boolean
  isSuccess: boolean
}

const DefaultPromptEditor: React.FC<DefaultPromptEditorProps> = ({
  defaultPrompt,
  onSave,
  isLoading,
  isSuccess,
}) => {
  const [prompt, setPrompt] = useState(defaultPrompt)
  const [hasChanges, setHasChanges] = useState(false)

  React.useEffect(() => {
    console.log('DefaultPromptEditor: useEffect triggered with defaultPrompt:', defaultPrompt)
    setPrompt(defaultPrompt)
    setHasChanges(false)
  }, [defaultPrompt])

  // Reset hasChanges when mutation succeeds
  React.useEffect(() => {
    if (isSuccess) {
      console.log('DefaultPromptEditor: Mutation successful, resetting hasChanges')
      setHasChanges(false)
    }
  }, [isSuccess])

  const handleChange = (value: string) => {
    setPrompt(value)
    setHasChanges(value !== defaultPrompt)
  }

  const handleSave = () => {
    onSave(prompt)
    // Don't reset hasChanges here - let the parent component handle it after successful mutation
  }

  const handleReset = () => {
    setPrompt(defaultPrompt)
    setHasChanges(false)
  }

  return (
    <div className="space-y-4">
      <textarea
        value={prompt}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Enter your default prompt for screenshot analysis..."
        className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        disabled={isLoading}
      />
      <div className="flex space-x-3">
        <button
          onClick={handleSave}
          disabled={!hasChanges || isLoading}
          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          onClick={handleReset}
          disabled={!hasChanges || isLoading}
          className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Reset
        </button>
      </div>
    </div>
  )
}

export default DashboardPage